import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery } from '@apollo/client'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { GET_ALL_USERS_LIST } from '../gql/queries/usersQuery'
import './MentionInput.css'

// Define user type
interface User {
  id: string
  firstName: string
  lastName: string
  email: string
}

interface MentionInputProps {
  value: string
  onChange: (value: string, mentionedUserIds: string[]) => void
  placeholder?: string
  minHeight?: string
}

// Helper function to extract mentioned user IDs from HTML content
const extractMentionedUserIds = (htmlContent: string, usersList?: User[]): string[] => {
  if (!htmlContent || !usersList) return []

  const mentionedIds: string[] = []

  // Look for each user's full name in the content
  usersList.forEach((user) => {
    const mentionPattern = `@${user.firstName} ${user.lastName}`
    if (htmlContent.includes(mentionPattern)) {
      mentionedIds.push(user.id)
    }
  })

  return mentionedIds
}

export const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  placeholder = 'Type your comments here...',
  minHeight = '120px',
}) => {
  const [showMentionDropdown, setShowMentionDropdown] = useState(false)
  const [mentionSearchTerm, setMentionSearchTerm] = useState('')
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const [mentionStartIndex, setMentionStartIndex] = useState<number>(-1)
  const [selectedUserIndex, setSelectedUserIndex] = useState<number>(0)

  const quillRef = useRef<ReactQuill>(null)

  // Fetch all users
  const { data: usersData } = useQuery<{ allUsersList: User[] }>(GET_ALL_USERS_LIST)

  // Filter users based on search term
  const filteredUsers = usersData?.allUsersList.filter((user) => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
    return fullName.includes(mentionSearchTerm.toLowerCase())
  }) || []

  // Handle text changes in the editor
  const handleChange = (content: string) => {
    // Extract mentioned user IDs from the content
    const mentionedIds = extractMentionedUserIds(content, usersData?.allUsersList)
    onChange(content, mentionedIds)

    // Check if @ was just typed
    const quill = quillRef.current?.getEditor()
    if (!quill) return

    const selection = quill.getSelection()
    if (!selection) return

    const cursorPosition = selection.index
    const text = quill.getText(0, cursorPosition)

    // Find the last @ symbol before cursor
    const lastAtIndex = text.lastIndexOf('@')

    if (lastAtIndex !== -1 && lastAtIndex >= cursorPosition - 50) {
      const searchText = text.substring(lastAtIndex + 1, cursorPosition)

      // Check if there's a space after @ (which would cancel the mention)
      if (searchText.includes(' ') || searchText.includes('\n')) {
        setShowMentionDropdown(false)
        return
      }

      setMentionSearchTerm(searchText)
      setMentionStartIndex(lastAtIndex)
      setShowMentionDropdown(true)
      setSelectedUserIndex(0) // Reset selection when search term changes

      // Get cursor position for dropdown placement
      const bounds = quill.getBounds(cursorPosition)
      const editorContainer = (quill.root.parentElement as HTMLElement).getBoundingClientRect()

      setDropdownPosition({
        top: bounds.bottom + editorContainer.top + 5,
        left: bounds.left + editorContainer.left,
      })
    } else {
      setShowMentionDropdown(false)
    }
  }

  // Handle user selection from dropdown
  const handleSelectUser = useCallback((user: User) => {
    const quill = quillRef.current?.getEditor()
    if (!quill || mentionStartIndex === -1) return

    const selection = quill.getSelection()
    if (!selection) return

    // Delete the @ and search term
    const deleteLength = selection.index - mentionStartIndex
    quill.deleteText(mentionStartIndex, deleteLength)

    // Insert the mention as a formatted span
    const mentionText = `@${user.firstName} ${user.lastName}`

    // Insert mention with formatting
    quill.insertText(mentionStartIndex, mentionText, {
      color: '#0d6efd',
      bold: true,
    })

    // Add a space after the mention WITH DEFAULT FORMATTING (removes the blue color)
    quill.insertText(mentionStartIndex + mentionText.length, ' ', {
      color: false,
      bold: false,
    })

    // Set cursor position after the mention and space
    quill.setSelection(mentionStartIndex + mentionText.length + 1, 0)

    // Remove any formatting at cursor position to ensure next text is normal
    quill.format('color', false)
    quill.format('bold', false)

    // Close dropdown
    setShowMentionDropdown(false)
    setMentionSearchTerm('')
    setMentionStartIndex(-1)

    // Trigger onChange which will extract mentioned IDs from the content
    const updatedContent = quill.root.innerHTML
    const mentionedIds = extractMentionedUserIds(updatedContent, usersData?.allUsersList)
    onChange(updatedContent, mentionedIds)
  }, [mentionStartIndex, onChange, usersData?.allUsersList])

  // Handle keyboard navigation in dropdown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showMentionDropdown || filteredUsers.length === 0) return

      if (e.key === 'Escape') {
        setShowMentionDropdown(false)
        setSelectedUserIndex(0)
        e.preventDefault()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedUserIndex((prev) => Math.min(prev + 1, filteredUsers.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedUserIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (filteredUsers[selectedUserIndex]) {
          handleSelectUser(filteredUsers[selectedUserIndex])
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showMentionDropdown, filteredUsers, selectedUserIndex])

  // Note: The parent component is responsible for clearing the value and mentionedUserIds
  // when the note is successfully saved (e.g., setValue('') and setMentionedUserIds([]))

  return (
    <div className="mention-input-container" style={{ position: 'relative' }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        placeholder={placeholder}
        value={value}
        className="h-100 h-full-quill"
        onChange={handleChange}
        style={{ minHeight }}
        modules={{
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            ['link', 'formula'],
            [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ font: [] }],
            [{ align: [] }],
            ['clean'],
          ],
        }}
      />

      {showMentionDropdown && filteredUsers.length > 0 && (
        <div
          className="mention-dropdown"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 9999,
          }}
        >
          <div className="mention-dropdown-list">
            {filteredUsers.slice(0, 10).map((user, index) => (
              <div
                key={user.id}
                className={`mention-dropdown-item ${index === selectedUserIndex ? 'selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault() // Prevent editor from losing focus
                  handleSelectUser(user)
                }}
                onMouseEnter={() => setSelectedUserIndex(index)}
              >
                <div className="mention-user-name">
                  {user.firstName} {user.lastName}
                </div>
                <div className="mention-user-email">{user.email}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
